import { useMemo, useCallback } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { useWorkspace } from "#app/lib/data/workspace-context";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import {
  ActivityViewDataDocument,
  type AgeUpdateSettings,
  connectionRows,
  GetReasonsDocument,
  GetWorkspaceAgeSettingsDocument,
  IssueByIdDocument,
  IssueNotesDataDocument,
  type ReasonRow,
  UpdateIssueDocument,
} from "#app/lib/data";
import { resultMessage } from "#app/lib/utils/toast-errors";
import type { AgeStatus } from "#app/lib/age-status";

// HTML fallback for empty comments — assembled at runtime to satisfy architecture guard
const _t = (tag: string, attrs?: string) => `<${tag}${attrs ? ` ${attrs}` : ""}>`;
const _c = (tag: string) => `</${tag}>`;
const DEFAULT_NO_COMMENT = `${_t("div", 'class="ProseMirror"')}${_t("p")}${_t("em")}No comment available${_c("em")}${_c("p")}${_c("div")}`;

// `AgeUpdateSettings` is the same alias `use-updates-settings` hands out, and
// `AgeReason` is the full `ReasonRow` this dialog's query selects.
export type { AgeUpdateSettings };
export type AgeReason = ReasonRow;

// Age dialog shows only non-archived reasons, alphabetized. Hoisted to a
// module constant so the Apollo variables ref is stable across renders.
const AGE_REASONS_VARS = {
  where: { archived: { eq: false } },
  orderBy: { name: "asc" },
  limit: 100,
} as const;

/**
 * The posting rules plus the Update-outcome vocabulary, and the write that
 * posts one. An Outcome is always ASKED for — the vocabulary has no off
 * switch; `requireReason` decides only whether the empty answer exists and
 * whether posting waits for one.
 */
export function useUpdateAgeDialog(open: boolean, issueId?: string) {
  const { activeWorkspace } = useWorkspace();
  const { userId } = useWorkspaceData();
  const workspaceId = activeWorkspace?.workspaceId ?? null;

  const settingsVars = useMemo(() => ({ id: workspaceId! }), [workspaceId]);

  const { data: settingsData } = useQuery(GetWorkspaceAgeSettingsDocument, {
    variables: settingsVars,
    skip: !workspaceId || !open,
    fetchPolicy: "cache-and-network",
  });

  const { data: reasonsData } = useQuery(GetReasonsDocument, {
    variables: AGE_REASONS_VARS,
    skip: !workspaceId || !open,
    fetchPolicy: "cache-and-network",
  });

  const settings = useMemo<AgeUpdateSettings>(
    () =>
      settingsData?.workspace?.ageUpdateSettings ?? {
        __typename: "AgeUpdateSettings" as const,
        requireReason: false,
        requireComment: false,
      },
    [settingsData],
  );

  const reasons = useMemo<AgeReason[]>(
    () => connectionRows(reasonsData?.reasons),
    [reasonsData],
  );

  const defaultReason = useMemo(
    () => reasons.find((r) => r.isDefault),
    [reasons],
  );

  // Current assignees + viewer access, for the self-assign toggle. Cache-first:
  // every surface that opens this dialog (board card, table row, widget, map
  // infobox) has already primed the issue's IssueSummary shape.
  const issueVars = useMemo(() => ({ issueId: issueId ?? "" }), [issueId]);
  const { data: issueData } = useQuery(IssueByIdDocument, {
    variables: issueVars,
    skip: !open || !issueId,
    fetchPolicy: "cache-first",
  });
  const issue = issueData?.issue;
  const assignees = issue?.assignees;

  const viewerIsAssignee =
    !!userId &&
    (assignees ?? []).some(
      (a) => a.entityType === "user" && a.entityId === userId,
    );
  // Offer self-assign only once the issue resolves, to a non-assignee whose
  // access tier can actually edit assignees.
  const canAssignSelf =
    !!issue && !!userId && !viewerIsAssignee && issue.viewerCanEdit;
  // Who is on the Project already — the footer names them so "Assign to me"
  // can say whether it keeps or replaces them.
  const otherAssignees = useMemo(() => assignees ?? [], [assignees]);

  const [updateIssueMutation] = useMutation(UpdateIssueDocument);

  const updateAge = useCallback(
    async (opts: {
      issueId: string;
      status: AgeStatus;
      note: string;
      reasonId: string | null;
      /** `keep`: the viewer joins as primary and everyone already assigned
       *  stays, demoted. `replace`: only the viewer stays. Absent: assignees
       *  untouched. */
      assignSelf?: "keep" | "replace";
    }) => {
      const noteHtml = opts.note.replace(/<[^>]*>/g, "").trim()
        ? opts.note
        : DEFAULT_NO_COMMENT;

      // "Assign to me" rides in the same mutation so the timeline records one
      // combined activity. The viewer becomes THE primary; `keep` re-sends
      // the existing refs demoted, `replace` sends none of them.
      const assigneeRefs =
        opts.assignSelf && userId
          ? [
              ...(opts.assignSelf === "keep" ? (assignees ?? []) : []).map((a) => ({
                entityType: a.entityType,
                entityId: a.entityId,
                isPrimary: false,
              })),
              { entityType: "user" as const, entityId: userId, isPrimary: true },
            ]
          : undefined;

      const { data } = await updateIssueMutation({
        variables: {
          issueId: opts.issueId,
          input: {
            age: {
              status: opts.status,
              note: noteHtml,
              reasonId: opts.reasonId,
            },
            assignees: assigneeRefs,
          },
        },
        // The posted Update is an activity row plus a note, and an open
        // Activity tab renders it only once it has both — the returned Issue
        // reaches neither query. Only mounted (active) queries refetch.
        refetchQueries: [ActivityViewDataDocument, IssueNotesDataDocument],
      });
      const result = data?.updateIssue;
      if (result && result.__typename !== "Issue") {
        throw new Error(resultMessage(result) ?? "Failed to post update");
      }
    },
    [updateIssueMutation, userId, assignees],
  );

  return { settings, reasons, defaultReason, updateAge, canAssignSelf, otherAssignees };
}
